import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, Check, ArrowRight, ArrowLeft, Shield, KeyRound, Server, FileCheck,
  Activity, DatabaseBackup, Globe, ScanSearch, PackageSearch, Wrench,
} from 'lucide-react';

const PILLARS = [
  {
    icon: Lock,
    title: 'Encrypted at rest & in transit',
    desc: 'AES-256 encryption at rest and TLS 1.3 in transit protects your data at every step — from your customer\u2019s browser to our database.',
  },
  {
    icon: KeyRound,
    title: 'Access security',
    desc: 'Multi-factor authentication with authenticator apps, passkeys and FIDO2 hardware security keys — plus phishing-resistant authentication for privileged systems. Role-based permissions mean your crew sees only what their role allows.',
  },
  {
    icon: Server,
    title: 'Secure infrastructure',
    desc: 'Hosted on enterprise-grade infrastructure with 24/7 monitoring, threat detection, encrypted daily backups and redundant storage.',
  },
  {
    icon: FileCheck,
    title: 'Compliance-minded',
    desc: 'Designed around GDPR and CCPA privacy principles, with SOC 2 on our roadmap. Payments are processed by Stripe — card data never touches our servers.',
  },
];

// Capability-organized reliability & security operations — how we actually run,
// not a wall of vendor logos.
const CAPABILITIES = [
  {
    icon: Activity,
    title: 'Continuous monitoring',
    items: [
      'Real-time application & performance monitoring',
      'Error tracking with automatic engineering alerts',
      'Service health checks running around the clock',
    ],
  },
  {
    icon: DatabaseBackup,
    title: 'Automatic backups',
    items: [
      'Daily encrypted backups to secure off-site storage',
      'Documented disaster recovery procedures',
      'Backup verification & tested restores',
    ],
  },
  {
    icon: Globe,
    title: 'Infrastructure protection',
    items: [
      'Enterprise-grade edge security & WAF',
      'DDoS protection, bot mitigation & rate limiting',
      'Global CDN with automatic TLS everywhere',
    ],
  },
  {
    icon: ScanSearch,
    title: 'Continuous security scanning',
    items: [
      'Every code change automatically analyzed before deployment',
      'Static application security testing on pull requests',
      'Secret detection in the development pipeline',
    ],
  },
  {
    icon: PackageSearch,
    title: 'Dependency monitoring',
    items: [
      'Third-party software continuously monitored for new vulnerabilities',
      'Automated dependency alerts & security patch reviews',
      'Prompt vulnerability remediation',
    ],
  },
  {
    icon: Wrench,
    title: 'Reliability engineering',
    items: [
      '99.9%+ uptime target on autoscaling infrastructure',
      'Redundant services with automatic failover',
      'Daily automated platform health test suites',
    ],
  },
];

const ROADMAP_CURRENT = [
  'FIDO2/WebAuthn-protected privileged access',
  'Multi-factor authentication (authenticator apps, passkeys, security keys)',
  'Dual-factor verification on dangerous admin actions',
  'Edge WAF & DDoS protection',
  'Continuous monitoring & alerting',
  'Daily encrypted backups',
  'Automated dependency monitoring',
  'Continuous code security scanning',
  'Audit logs on every action',
  'Responsible disclosure channel',
];

const ROADMAP_IN_PROGRESS = [
  'SOC 2 certification',
  'Independent penetration testing',
  'Bug bounty program',
];

const PROOF_CHIPS = [
  'Phishing-resistant admin authentication',
  'Encrypted backups',
  'Edge protection',
  'Secure payments through Stripe',
  'Audit logs on every action',
  '99.9%+ uptime target',
];

// Public, sales-shareable trust page — the long-form answer to
// "is BookingFlows going to do anything with my data?"
export default function SecurityPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="security-page">
      {/* Slim header */}
      <header className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors"
          data-testid="security-back-home"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          BookingFlows
        </button>
        <button
          onClick={() => navigate('/demo?src=security')}
          className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
          data-testid="security-demo-cta"
        >
          Talk to us
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-20 text-center">
        <div className="inline-flex items-center justify-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
          <Shield className="w-3.5 h-3.5" />
          Reliability &amp; security
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.08] mb-6">
          Your data is yours.{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Full stop.</span>
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
          BookingFlows is a software company. Our only business is your subscription —
          not your customer list, not your pricing, not your data.
        </p>
      </section>

      {/* Hero visual */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        <img
          src="/images/bf-security-hardening.webp"
          alt="Layered account security — hardware security keys, authenticator apps and multi-factor authentication protecting your data"
          className="w-full block rounded-3xl ring-1 ring-slate-900/[0.06]"
          data-testid="security-page-visual"
        />
      </section>

      {/* Siloed by design */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Siloed by design</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          Every company on BookingFlows runs in its own silo — a tenant-isolated data
          architecture with organization-scoped access controls. Your customers, bookings,
          revenue and expenses are visible to you and the team members you invite — and
          no one else. Not other operators. Not partners. Not us, beyond what it takes to
          run and support the platform.
        </p>
        <p className="text-gray-600 leading-relaxed">
          We never sell your data, never share it with third parties, and never use it to
          compete with you. You can export everything at any time, and if you ever leave,
          you can take your data with you and ask us to delete the rest.
        </p>
      </section>

      {/* Four pillars */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-10">
          {PILLARS.map((p) => (
            <div key={p.title} className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <p.icon className="w-5 h-5" />
              </span>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1.5">{p.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How we operate — capability grid */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20" data-testid="security-capabilities">
        <div className="text-center mb-12">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2">How we operate</h2>
          <p className="text-sm text-gray-500 max-w-xl mx-auto">
            Most software says &quot;secure.&quot; Here&apos;s what actually runs behind BookingFlows,
            every day.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CAPABILITIES.map((c) => (
            <div key={c.title} className="rounded-2xl bg-gray-50 ring-1 ring-slate-900/[0.05] p-6">
              <span className="w-9 h-9 rounded-xl bg-white ring-1 ring-slate-900/[0.06] text-blue-600 flex items-center justify-center mb-4">
                <c.icon className="w-4.5 h-4.5" />
              </span>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{c.title}</h3>
              <ul className="space-y-2">
                {c.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[13px] text-gray-600 leading-snug">
                    <Check className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" strokeWidth={2.5} />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Security roadmap — current vs in progress */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20" data-testid="security-roadmap">
        <div className="rounded-3xl bg-[#0f172a] p-8 sm:p-12 text-white">
          <h2 className="text-lg font-semibold mb-2">Security roadmap</h2>
          <p className="text-sm text-slate-400 mb-8 max-w-xl">
            Security is never &quot;done.&quot; Here&apos;s what&apos;s live today and what we&apos;re
            actively working toward — no perfection claims, just the honest state of play.
          </p>
          <div className="grid sm:grid-cols-2 gap-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-4">Live today</p>
              <ul className="space-y-2.5">
                {ROADMAP_CURRENT.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <span className="w-4.5 h-4.5 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
                    </span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-amber-400 font-bold mb-4">In progress</p>
              <ul className="space-y-2.5">
                {ROADMAP_IN_PROGRESS.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <span className="w-4.5 h-4.5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    </span>
                    {it}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-6 leading-relaxed">
                Found a vulnerability? Report it through our{' '}
                <a href="/support?category=Security%20%26%20Vulnerability%20Reports#tickets" className="text-slate-300 underline underline-offset-2" data-testid="security-disclosure-link">
                  Support Center
                </a>{' '}
                under &ldquo;Security &amp; Vulnerability Reports&rdquo; — we investigate every good-faith report. See our{' '}
                <a href="/vulnerability-disclosure" className="text-slate-300 underline underline-offset-2" data-testid="security-vdp-link">
                  Vulnerability Disclosure Policy
                </a>{' '}
                and{' '}
                <a href="/security-policy" className="text-slate-300 underline underline-offset-2" data-testid="security-policy-link">
                  Security Policy
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Proof chips */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
          {PROOF_CHIPS.map((t) => (
            <div key={t} className="flex items-center gap-2">
              <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="text-gray-700 text-sm sm:text-base">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-24 text-center">
        <p className="text-gray-500 mb-6">
          Questions about how we handle your data? We answer them plainly.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-8">
          <button
            onClick={() => navigate('/demo?src=security')}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
            data-testid="security-footer-demo-cta"
          >
            Book a demo
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => navigate('/privacy')}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors"
            data-testid="security-privacy-link"
          >
            Read the privacy policy
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>
    </div>
  );
}
